import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { CalendarDays, BookOpen, Users as UsersIcon, BarChart3, Package2, Sparkles } from "lucide-react";
import { MonthPicker } from "../components/management/MonthPicker";
import { DayMinSpinner } from "../components/management/DayMinSpinner";
import { DetailsDialog } from "../components/management/DetailsDialog";
import { regieDefaultMinForIso, TOOL_SET_HEX } from "./planificator/utils";
import { ToolColorDots, toolColorsOfEntries, toolColorsBg } from "../components/ToolColorDots";
import { LogHoursDialog } from "../components/management/LogHoursDialog";
import { BulkAssignDialog } from "../components/management/BulkAssignDialog";
import { Jurnal } from "../components/management/Jurnal";
import { Charts } from "../components/management/Charts";
import { Containers } from "../components/management/Containers";
import { HoursGridWrapper } from "../components/management/HoursGridWrapper";
import { exportPlanPDF } from "../components/management/exportPlanPDF";
import { ScheduleDialog } from "../components/management/ScheduleDialog";
import { ClientOverrideDialog } from "../components/management/ClientOverrideDialog";

const today = new Date();
const DEFAULT_MONTH = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;

// Formatare ISO LOCALĂ (fără conversie UTC!). toISOString() muta data cu o zi înapoi
// pentru utilizatorii din fusuri orare +1/+2/+3 (CH/RO) → week_start și alocările pe zile
// erau decalate, deci minimul necesar era calculat greșit.
const localIso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

// Monday-based week start for a given date
const mondayOf = (isoDate) => {
  const d = new Date(isoDate + "T00:00:00");
  const dow = d.getDay(); // 0=Sun .. 6=Sat
  const diff = (dow + 6) % 7; // days since Monday
  d.setDate(d.getDate() - diff);
  return localIso(d);
};

// Construiește un sumar scurt despre ce-i de făcut pe un proiect:
//   ex: "3 zone EKAS (45 m²) · 1 Factsheet (12 m²) · Total 57 m²"
//   + secțiune separată cu materialele de scos: "Deckputz 15m² · Grundputz 18.5m² · Asbest 12m²"
// - Folosește `scope_items` (qty per material) când există → date exacte
// - Fallback: numără zonele din `project.zones` și caută m² în text (work_zones / descriere)
function buildProjectSummary(project) {
  if (!project) return null;
  const zones = Array.isArray(project.zones) ? project.zones : [];
  const scopeItems = Array.isArray(project.scope_items) ? project.scope_items : [];

  // — Grupare pe material: cantitate per unitate + nr de zone distincte în care apare
  const byMaterial = new Map();
  for (const it of scopeItems) {
    const mat = (it?.material || "").trim();
    if (!mat) continue;
    const qty = parseFloat(it?.qty) || 0;
    if (qty <= 0) continue;
    const unit = ((it?.unit || "m²").trim()) || "m²";
    const zoneIdx = Number(it?.zone_index) > 0 ? Number(it.zone_index) : 1;
    const e = byMaterial.get(mat) || { material: mat, byUnit: {}, zones: new Set() };
    e.byUnit[unit] = (e.byUnit[unit] || 0) + qty;
    e.zones.add(zoneIdx);
    byMaterial.set(mat, e);
  }
  const fmt = (v) => v.toLocaleString("ro-RO", { maximumFractionDigits: 1 });
  const materialLines = Array.from(byMaterial.values())
    .map((e) => {
      const qtyParts = Object.entries(e.byUnit)
        .filter(([, v]) => v > 0)
        .map(([u, v]) => `${fmt(v)} ${u}`);
      const zoneCount = e.zones.size;
      const zoneSuffix = zoneCount > 1 ? ` · în ${zoneCount} zone` : "";
      return {
        material: e.material,
        text: `${e.material}: ${qtyParts.join(" + ")}${zoneSuffix}`,
        totalQty: Object.values(e.byUnit).reduce((s, v) => s + v, 0),
      };
    })
    .sort((a, b) => b.totalQty - a.totalQty);

  // — Badge zone (din project.zones, agregat pe tip)
  const zoneCountsByType = {};
  for (const z of zones) {
    const t = (z?.type || z?.label || "Altele").trim() || "Altele";
    zoneCountsByType[t] = (zoneCountsByType[t] || 0) + 1;
  }
  const zoneBadge = Object.entries(zoneCountsByType)
    .map(([t, c]) => `${c} ${t}`)
    .join(" · ");

  return {
    zoneBadge: zoneBadge || null,
    materialLines: materialLines.length > 0 ? materialLines : null,
    hasContent: materialLines.length > 0 || !!zoneBadge,
  };
}

function PeoplePlanGrid() {
  const { canDo } = useAuth();
  const navigate = useNavigate();
  const [month, setMonth] = useState(DEFAULT_MONTH);
  const [persons, setPersons] = useState([]);
  const [entries, setEntries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [regii, setRegii] = useState([]);
  const [allCars, setAllCars] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [vacations, setVacations] = useState([]);
  const [hoursMap, setHoursMap] = useState({});
  const [search, setSearch] = useState("");
  const [dialog, setDialog] = useState(null);

  const [y, m] = month.split("-").map(Number);
  const daysInMonth = new Date(y, m, 0).getDate();
  const days = useMemo(() => Array.from({ length: daysInMonth }, (_, i) => i + 1), [daysInMonth]);

  // === Săptămâni complete: include zile din luna anterioară / următoare pentru a vedea săptămâna integral ===
  const getISOWeek = (date) => {
    const target = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNr = (target.getUTCDay() + 6) % 7;
    target.setUTCDate(target.getUTCDate() - dayNr + 3);
    const firstThursday = new Date(Date.UTC(target.getUTCFullYear(), 0, 4));
    const diff = (target - firstThursday) / 86400000;
    return 1 + Math.round((diff - 3 + ((firstThursday.getUTCDay() + 6) % 7)) / 7);
  };

  const weekCells = useMemo(() => {
    const first = new Date(y, m - 1, 1);
    const start = new Date(first);
    start.setDate(first.getDate() - ((first.getDay() + 6) % 7));   // Luni ce conține ziua 1
    const last = new Date(y, m - 1, daysInMonth);
    const end = new Date(last);
    end.setDate(last.getDate() + (6 - ((last.getDay() + 6) % 7))); // Duminică ce conține ultima zi
    const cells = [];
    const cursor = new Date(start);
    while (cursor <= end) {
      cells.push({
        d: cursor.getDate(),
        iso: `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, "0")}-${String(cursor.getDate()).padStart(2, "0")}`,
        year: cursor.getFullYear(),
        monthIdx: cursor.getMonth(), // 0..11
        inMonth: cursor.getMonth() === m - 1 && cursor.getFullYear() === y,
        weekday: cursor.getDay(),
      });
      cursor.setDate(cursor.getDate() + 1);
    }
    return cells;
  }, [y, m, daysInMonth]);

  // Grupare pe săptămâni (pentru header-ul KW)
  const weekGroups = useMemo(() => {
    const groups = [];
    let cur = null;
    for (const c of weekCells) {
      if (c.weekday === 1 || groups.length === 0) {
        const d = new Date(c.iso + "T00:00:00");
        cur = { kw: getISOWeek(d), year: d.getFullYear(), cells: [], firstIso: c.iso };
        groups.push(cur);
      }
      cur.cells.push(c);
    }
    return groups;
  }, [weekCells]);

  // Map secundar pe ISO date pentru ghost cells (zile din lunile vecine, vizibile în săptămâna care străbate luna).
  // Entries returnate de /schedule pentru luna curentă includ și intrările care străbat granițele de lună.
  const assignedByIsoProject = useMemo(() => {
    const map = {};
    for (const e of entries) {
      if (!e.project_id || !e.start_date) continue;
      const start = new Date(e.start_date + "T00:00:00");
      const end = new Date((e.end_date || e.start_date) + "T00:00:00");
      const persons = Array.isArray(e.person_ids) ? e.person_ids.length : (e.person_count || 0);
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const iso = localIso(d);
        map[e.project_id] = map[e.project_id] || {};
        const prev = map[e.project_id][iso] || { count: 0, hasEntry: false, color: null, allocation_color: null };
        map[e.project_id][iso] = {
          count: prev.count + persons,
          hasEntry: true,
          color: prev.color || e.color || null,
          allocation_color: prev.allocation_color || e.allocation_color || null,
        };
      }
    }
    return map;
  }, [entries]);

  const assignedByIsoRegie = useMemo(() => {
    const map = {};
    for (const e of entries) {
      if (!e.regie_id || !e.start_date) continue;
      const start = new Date(e.start_date + "T00:00:00");
      const end = new Date((e.end_date || e.start_date) + "T00:00:00");
      const persons = Array.isArray(e.person_ids) ? e.person_ids.length : (e.person_count || 0);
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const iso = localIso(d);
        map[e.regie_id] = map[e.regie_id] || {};
        const prev = map[e.regie_id][iso] || { count: 0, hasEntry: false, color: null };
        map[e.regie_id][iso] = {
          count: prev.count + persons,
          hasEntry: true,
          color: prev.color || e.color || null,
        };
      }
    }
    return map;
  }, [entries]);

  // Render helper pentru celule zile non-existente în lună (ghost cells)
  const renderDayHeader = (cell, renderFn) => {
    if (!cell.inMonth) {
      return (
        <th
          key={`gh-${cell.iso}`}
          className="text-center text-[10px] text-zinc-400 font-normal border-r border-zinc-200 min-w-[34px] bg-white"
          title={`${cell.d}/${cell.monthIdx + 1} — în afara lunii curente`}
          data-testid={`ghost-day-${cell.iso}`}
        >
          {cell.d}
        </th>
      );
    }
    return renderFn(cell.d, cell.iso);
  };

  // Render helper pentru rânduri cu PROIECTE — celulele ghost arată bara colorată dacă proiectul are alocări pe acea ISO date.
  const renderProjectGhostCell = (cell, projectId, color = "#0EA5E9") => {
    const info = (assignedByIsoProject[projectId] || {})[cell.iso] || null;
    const count = info ? info.count : 0;
    const hasEntry = info ? info.hasEntry : false;
    const barColor = (info && info.allocation_color && TOOL_SET_HEX[info.allocation_color]) || (info && info.color) || color || "#3B82F6";
    return (
      <td
        key={`gh-pj-${projectId}-${cell.iso}`}
        className="p-0.5 border-r border-zinc-200 min-w-[34px] h-7 relative bg-white"
        data-testid={`ghost-proj-${projectId}-${cell.iso}`}
      >
        {hasEntry ? (
          <div
            className="absolute inset-0 rounded-sm overflow-hidden opacity-70"
            style={{ background: barColor }}
            title={`Continuare alocare (${count} ${count === 1 ? "pers" : "pers"}) — zi din ${cell.d}/${cell.monthIdx + 1}`}
          >
            {/* Linia diagonală pentru consistență vizuală cu celulele in-month */}
            <span className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(to top right, transparent calc(50% - 0.5px), rgba(255,255,255,0.45) calc(50% - 0.5px), rgba(255,255,255,0.45) calc(50% + 0.5px), transparent calc(50% + 0.5px))" }} />
            {count > 0 && (
              <span className="absolute top-0 left-0.5 text-[9px] font-extrabold leading-none pt-0.5 text-white">{count}</span>
            )}
          </div>
        ) : (
          <span className="absolute inset-0 flex items-center justify-center text-[10px] text-zinc-300" title={`${cell.d}/${cell.monthIdx + 1}`}>{cell.d}</span>
        )}
      </td>
    );
  };

  // Render helper pentru rânduri cu REGII — analog.
  const renderRegieGhostCell = (cell, regieId, color = "#A855F7") => {
    const info = (assignedByIsoRegie[regieId] || {})[cell.iso] || null;
    const count = info ? info.count : 0;
    const hasEntry = info ? info.hasEntry : false;
    const barColor = (info && info.color) || color || "#F59E0B";
    return (
      <td
        key={`gh-rg-${regieId}-${cell.iso}`}
        className="p-0.5 border-r border-zinc-200 min-w-[34px] h-8 relative bg-white"
        data-testid={`ghost-regie-${regieId}-${cell.iso}`}
      >
        {hasEntry ? (
          <div
            className="absolute inset-0 rounded-sm overflow-hidden opacity-70"
            style={{ background: barColor }}
            title={`Continuare regie (${count} ${count === 1 ? "pers" : "pers"}) — zi din ${cell.d}/${cell.monthIdx + 1}`}
          >
            <span className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(to top right, transparent calc(50% - 0.5px), rgba(255,255,255,0.45) calc(50% - 0.5px), rgba(255,255,255,0.45) calc(50% + 0.5px), transparent calc(50% + 0.5px))" }} />
            {count > 0 && (
              <span className="absolute top-0 left-0.5 text-[9px] font-extrabold leading-none pt-0.5 text-white">{count}</span>
            )}
          </div>
        ) : (
          <span className="absolute inset-0 flex items-center justify-center text-[10px] text-zinc-300" title={`${cell.d}/${cell.monthIdx + 1}`}>{cell.d}</span>
        )}
      </td>
    );
  };

  const renderDayCell = (cell, renderFn, opts = {}) => {
    if (!cell.inMonth) {
      const { kind, refId, color } = opts;
      if (kind === "project" && refId) return renderProjectGhostCell(cell, refId, color);
      if (kind === "regie" && refId) return renderRegieGhostCell(cell, refId, color);
      return (
        <td
          key={`gh-td-${cell.iso}`}
          className="text-center text-[10px] border-r border-zinc-200 min-w-[34px] bg-white text-zinc-300"
        >
        </td>
      );
    }
    return renderFn(cell.d, cell.iso);
  };

  const load = async () => {
    // Folosim window-ul vizibil REAL (include ghost days din lunile vecine)
    // ca să apară TOATE proiectele/regiile active în săptămânile complete afișate.
    const fromIso = (weekCells && weekCells.length > 0) ? weekCells[0].iso : `${month}-01`;
    const toIso = (weekCells && weekCells.length > 0) ? weekCells[weekCells.length - 1].iso : `${month}-${String(daysInMonth).padStart(2, "0")}`;
    const [s, p, r, ps, hol, vacs, cars, hm] = await Promise.all([
      api.get(`/schedule?from_date=${fromIso}&to_date=${toIso}`),
      api.get("/projects"),
      api.get("/regii"),
      api.get("/persons").catch(() => ({ data: [] })),
      api.get(`/holidays?year=${y}`).catch(() => ({ data: [] })),
      api.get("/vacations").catch(() => ({ data: [] })),
      api.get("/cars").catch(() => ({ data: [] })),
      api.get(`/management/hours-map?month=${month}`).catch(() => ({ data: {} })),
    ]);
    setEntries(s.data || []);
    setProjects(p.data || []);
    setRegii(r.data || []);
    setPersons(ps.data || []);
    setHolidays(hol.data || []);
    setVacations(vacs.data || []);
    setAllCars(cars.data || []);
    setHoursMap(hm.data || {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month]);

  // Navigare luna: prev/next cu butoane + tastatură ← →
  const shiftMonth = (delta) => {
    const [yy, mm] = month.split("-").map(Number);
    const d = new Date(yy, (mm - 1) + delta, 1);
    const newMonth = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    setMonth(newMonth);
  };
  useEffect(() => {
    const onKey = (e) => {
      if (e.target && /input|textarea|select/i.test(e.target.tagName || "")) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === "ArrowLeft") { e.preventDefault(); shiftMonth(-1); }
      else if (e.key === "ArrowRight") { e.preventDefault(); shiftMonth(1); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month]);

  const holidaysByDate = useMemo(() => {
    const x = {};
    for (const h of holidays) {
      if (x[h.date]) x[h.date] = { ...x[h.date], extra: [...(x[h.date].extra || []), h] };
      else x[h.date] = { ...h, extra: [] };
    }
    return x;
  }, [holidays]);

  // Sort persons alphabetically by surname+name (active first, inactive last greyed)
  const sortedPersons = useMemo(() => {
    const list = [...persons].filter((p) => p.active !== false && !p.data_concedierii);
    list.sort((a, b) => {
      const aN = `${a.nume || ""} ${a.prenume || ""}`.toLowerCase().trim();
      const bN = `${b.nume || ""} ${b.prenume || ""}`.toLowerCase().trim();
      return aN.localeCompare(bN, "ro");
    });
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((p) => `${p.nume || ""} ${p.prenume || ""}`.toLowerCase().includes(q));
  }, [persons, search]);

  // Personal birou (flag pe persoană) — apare în tabel dar NU se numără la "disponibili pentru muncă"
  const isOfficePerson = (p) => p.office_staff === true;
  const fieldPersonsCount = useMemo(() => sortedPersons.filter((p) => !isOfficePerson(p)).length, [sortedPersons]); // eslint-disable-line

  // Index entries by person for fast lookup
  const entriesByPerson = useMemo(() => {
    const x = {};
    for (const e of entries) {
      for (const pid of (e.person_ids || [])) {
        if (!x[pid]) x[pid] = [];
        x[pid].push(e);
      }
    }
    return x;
  }, [entries]);

  // Vacations index by person
  const vacsByPerson = useMemo(() => {
    const x = {};
    for (const v of vacations) {
      if (v.status !== "approved") continue;
      if (!x[v.person_id]) x[v.person_id] = [];
      x[v.person_id].push(v);
    }
    return x;
  }, [vacations]);

  const entriesForPersonDay = (pid, dateStr) => {
    const list = entriesByPerson[pid] || [];
    return list.filter((e) => (e.start_date || "") <= dateStr && dateStr <= (e.end_date || ""));
  };

  const isOnVacation = (pid, dateStr) => {
    const list = vacsByPerson[pid] || [];
    return list.some((v) => (v.start_date || "") <= dateStr && dateStr <= (v.end_date || ""));
  };

  // Persoane libere AZI (azi e în luna curentă)
  const todayIso = localIso(new Date());
  const isCurrentMonth = todayIso.startsWith(month);
  const freeTodayPersons = useMemo(() => {
    if (!isCurrentMonth) return [];
    return sortedPersons.filter((p) => {
      if (isOnVacation(p.id, todayIso)) return false;
      const ents = entriesForPersonDay(p.id, todayIso);
      return ents.length === 0;
    });
    // eslint-disable-next-line
  }, [sortedPersons, entriesByPerson, vacsByPerson, todayIso, isCurrentMonth]);

  const [bulkOpen, setBulkOpen] = useState(false);
  const [hoursDialog, setHoursDialog] = useState(null); // { person_id, date, name }
  const [detailDialog, setDetailDialog] = useState(null); // { kind: 'regie'|'project', data }

  const onCellClick = (pid, day) => {
    if (!canDo("MANAGEMENT", "edit")) return;
    const dateStr = `${month}-${String(day).padStart(2, "0")}`;
    const todayStr = localIso(new Date());
    const existingHours = (hoursMap[pid] || {})[dateStr];
    // Past day OR today with hours already logged → log/edit hours dialog
    if (dateStr <= todayStr) {
      const person = sortedPersons.find((x) => x.id === pid);
      setHoursDialog({
        person_id: pid,
        date: dateStr,
        name: person ? `${person.nume} ${person.prenume}` : "—",
      });
      return;
    }
    const existing = entriesForPersonDay(pid, dateStr);
    if (existing.length === 1) {
      setDialog({ mode: "edit", entry: existing[0] });
    } else if (existing.length > 1) {
      setDialog({ mode: "edit", entry: existing[0] });
    } else {
      setDialog({ mode: "new", start_date: dateStr, end_date: dateStr, prefilledPerson: pid });
    }
  };

  // Regii: toate cele active (chiar și fără alocări) + entries pentru luna curentă, grupate pe firmă
  const regiiInMonth = useMemo(() => {
    const entriesByRegie = {};
    for (const e of entries) {
      if (!e.is_regie) continue;
      const key = e.regie_id || e.label;
      if (!entriesByRegie[key]) entriesByRegie[key] = [];
      entriesByRegie[key].push(e);
    }
    const out = [];
    const monthLast = `${month}-${String(daysInMonth).padStart(2, "0")}`;
    const monthFirst = `${month}-01`;
    for (const r of regii) {
      if (r.active === false) continue;
      const sd = r.start_date;
      const ed = r.end_date;
      const ok = (!sd || sd <= monthLast) && (r.indeterminate_end || !ed || ed >= monthFirst);
      if (!ok && !entriesByRegie[r.id]) continue;
      out.push({
        ...r,
        firma: r.firma || (r.name || "").split(" ")[0] || "—",
        entries: entriesByRegie[r.id] || [],
      });
    }
    for (const [key, ents] of Object.entries(entriesByRegie)) {
      if (out.find((x) => x.id === key)) continue;
      out.push({ id: key, name: ents[0].label, firma: (ents[0].label || "").split(" ")[0] || "—", color: ents[0].color, entries: ents });
    }
    return out.sort((a, b) => {
      const fc = (a.firma || "").localeCompare(b.firma || "", "ro");
      return fc !== 0 ? fc : (a.name || "").localeCompare(b.name || "", "ro");
    });
  }, [entries, regii, month, daysInMonth]);

  // Grupare pe firmă pentru randare
  const regiiGrouped = useMemo(() => {
    const groups = {};
    for (const r of regiiInMonth) {
      const f = r.firma || "—";
      if (!groups[f]) groups[f] = [];
      groups[f].push(r);
    }
    return Object.entries(groups).sort((a, b) => a[0].localeCompare(b[0], "ro"));
  }, [regiiInMonth]);

  // Categoriile „confirmate" = cat 5+ (5, 6, 6.1, 7). Sub 5 = neconfirmate, nu apar în Management.
  const MGMT_VISIBLE_CATS = ["5", "6", "6.1", "7"];

  const projectsInMonth = useMemo(() => {
    const x = {};
    for (const e of entries) {
      if (e.is_regie) continue;
      const key = e.project_id || e.label;
      if (!x[key]) {
        const proj = projects.find((p) => p.id === e.project_id);
        // Skip proiectele neconfirmate (cat < 5)
        if (proj && !MGMT_VISIBLE_CATS.includes(String(proj.category || ""))) continue;
        x[key] = {
          id: e.project_id,
          name: proj?.name || e.label,
          color: e.color,
          // Pentru sortare cronologică pe coloana din stânga: folosim start_date din proiect
          // (sau, dacă lipsește, prima dată din intrările din schedule).
          // Proiectele fără dată merg la sfârșit (alfabetic ca tie-breaker).
          start_date: proj?.start_date || null,
          end_date: proj?.end_date || null,
          entries: [],
        };
      }
      x[key].entries.push(e);
      // Fallback: dacă proiectul nu are start_date, folosim minimul din schedule entries
      if (!x[key].start_date && e.start_date) {
        if (!x[key]._fallback_start || e.start_date < x[key]._fallback_start) {
          x[key]._fallback_start = e.start_date;
        }
      }
    }
    const list = Object.values(x).map((p) => ({
      ...p,
      _sort_date: p.start_date || p._fallback_start || null,
    }));
    return list.sort((a, b) => {
      // Cronologic asc după start_date; cele fără dată merg la sfârșit
      if (a._sort_date && b._sort_date) {
        if (a._sort_date !== b._sort_date) return a._sort_date < b._sort_date ? -1 : 1;
        return (a.name || "").localeCompare(b.name || "", "ro");
      }
      if (a._sort_date) return -1;
      if (b._sort_date) return 1;
      return (a.name || "").localeCompare(b.name || "", "ro");
    });
  }, [entries, projects]);

  const dayHasEntry = (entriesList, dateStr) => entriesList.find((e) => (e.start_date || "") <= dateStr && dateStr <= (e.end_date || ""));

  const onTopRowClick = (entry, dateStr, isRegie, refId, label, color) => {
    if (!canDo("MANAGEMENT", "edit")) return;
    if (entry) {
      setDialog({ mode: "edit", entry });
    } else {
      // Auto-extend end_date la finalul proiectului/regiei (sau finalul lunii)
      let endDate = dateStr;
      if (isRegie) {
        const r = regii.find((x) => x.id === refId);
        if (r) {
          if (!r.indeterminate_end && r.end_date && r.end_date >= dateStr) endDate = r.end_date;
          else {
            // capăt luna curentă
            const lastDay = new Date(y, m, 0).getDate();
            endDate = `${month}-${String(lastDay).padStart(2, "0")}`;
          }
        }
      } else {
        const p = projects.find((x) => x.id === refId);
        if (p && p.end_date && p.end_date >= dateStr) endDate = p.end_date;
        else {
          const lastDay = new Date(y, m, 0).getDate();
          endDate = `${month}-${String(lastDay).padStart(2, "0")}`;
        }
      }
      const e = { project_id: isRegie ? null : refId, regie_id: isRegie ? refId : null, label, color, is_regie: isRegie, start_date: dateStr, end_date: endDate, person_ids: [], car_ids: [] };
      setDialog({ mode: "new", start_date: dateStr, end_date: endDate, prefillEntry: e });
    }
  };

  // Calcul rânduri sumar (per zi) - planificat efectiv
  const summaryRows = useMemo(() => {
    // Total persoane planificate per zi
    const totalAssigned = {};
    // Persoane planificate pe regii per zi
    const regieAssigned = {};
    // Persoane în concediu per zi
    const onVacation = {};
    const onVacationField = {}; // doar personal de teren (fără birou)
    // 🟢 Persoane alocate per regie/proiect, per zi (pentru calcul deficit)
    const assignedByRegie = {};   // { regie_id: { day: count } }
    const assignedByProject = {}; // { project_id: { day: count } }
    for (const d of days) {
      const dStr = `${month}-${String(d).padStart(2, "0")}`;
      const setT = new Set();
      const setR = new Set();
      let vc = 0;
      let vcField = 0;
      for (const e of entries) {
        if ((e.start_date || "") <= dStr && dStr <= (e.end_date || "")) {
          const personCount = (e.person_ids || []).length;
          for (const pid of (e.person_ids || [])) {
            setT.add(pid);
            if (e.is_regie) setR.add(pid);
          }
          if (e.is_regie && e.regie_id) {
            if (!assignedByRegie[e.regie_id]) assignedByRegie[e.regie_id] = {};
            assignedByRegie[e.regie_id][d] = (assignedByRegie[e.regie_id][d] || 0) + personCount;
          } else if (e.project_id) {
            if (!assignedByProject[e.project_id]) assignedByProject[e.project_id] = {};
            assignedByProject[e.project_id][d] = (assignedByProject[e.project_id][d] || 0) + personCount;
          }
        }
      }
      for (const p of sortedPersons) {
        if (isOnVacation(p.id, dStr)) {
          vc++;
          if (!isOfficePerson(p)) vcField++;
        }
      }
      totalAssigned[d] = setT.size;
      regieAssigned[d] = setR.size;
      onVacation[d] = vc;
      onVacationField[d] = vcField;
    }
    return { totalAssigned, regieAssigned, onVacation, onVacationField, assignedByRegie, assignedByProject };
  }, [days, entries, month, sortedPersons, vacsByPerson]); // eslint-disable-line

  // Necesar per zi (din regii_weeks min_people + projects.min_people)
  const [regieWeeksMap, setRegieWeeksMap] = useState({});
  // Day-level overrides: { regii: {regie_id: {date: {min, max}}}, projects: {project_id: {date: {min, max}}} }
  const [dayOverrides, setDayOverrides] = useState({ regii: {}, projects: {} });
  // Client-level overrides per zi: { [clientName]: { [date]: min_people } }
  const [clientOverrides, setClientOverrides] = useState({});
  // Dialog pentru editarea override-ului pe interval pentru un client
  const [clientOvDialog, setClientOvDialog] = useState(null); // { clientName, defaultStart, defaultEnd, defaultValue }
  useEffect(() => {
    const fetchAll = async () => {
      try {
        const promises = regii.filter((r) => r.active !== false).map((r) =>
          api.get(`/regii/${r.id}/weeks?month=${month}`).then((res) => ({ id: r.id, weeks: res.data || [] })).catch(() => ({ id: r.id, weeks: [] }))
        );
        const all = await Promise.all(promises);
        const map = {};
        for (const x of all) map[x.id] = x.weeks;
        setRegieWeeksMap(map);
        // Fetch day overrides
        const r = await api.get(`/management/day-overrides?month=${month}`).catch(() => null);
        if (r?.data) {
          const reg = {};
          for (const o of r.data.regii || []) {
            if (!reg[o.regie_id]) reg[o.regie_id] = {};
            reg[o.regie_id][o.date] = { min: o.min_people, max: o.max_people };
          }
          const prj = {};
          for (const o of r.data.projects || []) {
            if (!prj[o.project_id]) prj[o.project_id] = {};
            prj[o.project_id][o.date] = { min: o.min_people, max: o.max_people };
          }
          setDayOverrides({ regii: reg, projects: prj });
        }
        // Fetch client overrides
        const co = await api.get(`/management/client-overrides?month=${month}`).catch(() => null);
        if (co?.data?.items) {
          const cmap = {};
          for (const it of co.data.items) {
            const k = (it.client_name || "").trim();
            if (!k) continue;
            if (!cmap[k]) cmap[k] = {};
            cmap[k][it.date] = it.min_people;
          }
          setClientOverrides(cmap);
        } else {
          setClientOverrides({});
        }
      } catch { /* noop */ }
    };
    if (regii.length) fetchAll();
  }, [regii, month]);

  // Helper: salvează override per-zi pe regie sau proiect
  const updateDayOverride = async (kind, id, day, field, newVal) => {
    try {
      const url = kind === "regie" ? `/regii/${id}/days/${day}` : `/projects/${id}/days/${day}`;
      const body = field === "min" ? { min_people: newVal } : { max_people: newVal };
      await api.put(url, body);
      setDayOverrides((prev) => {
        const next = { regii: { ...prev.regii }, projects: { ...prev.projects } };
        const bucket = kind === "regie" ? "regii" : "projects";
        if (!next[bucket][id]) next[bucket][id] = {};
        next[bucket][id] = {
          ...next[bucket][id],
          [day]: {
            ...(next[bucket][id][day] || {}),
            [field]: newVal,
          },
        };
        return next;
      });
    } catch (e) {
      toast.error("Eroare la actualizare");
    }
  };

  // ===== Client (Auftraggeber) helpers =====
  // Extrage numele de client pentru o regie sau proiect
  const _projClient = (p) => (p?.auftraggeber || "").trim();
  const _regieClient = (r) => (r?.auftraggeber || r?.firma || "").trim();

  // Mapă client → { regii: [...], projects: [...] }
  const clientsByName = useMemo(() => {
    const map = {};
    for (const r of regii) {
      if (r.active === false) continue;
      const k = _regieClient(r);
      if (!k) continue;
      if (!map[k]) map[k] = { regii: [], projects: [] };
      map[k].regii.push(r);
    }
    for (const p of projects) {
      if (p.status === "Demontat") continue;
      const k = _projClient(p);
      if (!k) continue;
      if (!map[k]) map[k] = { regii: [], projects: [] };
      map[k].projects.push(p);
    }
    return map;
  }, [regii, projects]);

  // Lista de clienți care AU regii sau proiecte VS clienți care au DOAR overrides (fără să fi fost legați)
  const clientNames = useMemo(() => {
    const set = new Set();
    for (const k of Object.keys(clientsByName)) set.add(k);
    for (const k of Object.keys(clientOverrides || {})) set.add(k);
    return Array.from(set).sort((a, b) => a.localeCompare(b, "ro"));
  }, [clientsByName, clientOverrides]);

  // Reload helper
  const reloadClientOverrides = async () => {
    try {
      const co = await api.get(`/management/client-overrides?month=${month}`);
      const cmap = {};
      for (const it of (co.data?.items || [])) {
        const k = (it.client_name || "").trim();
        if (!k) continue;
        if (!cmap[k]) cmap[k] = {};
        cmap[k][it.date] = it.min_people;
      }
      setClientOverrides(cmap);
    } catch { /* noop */ }
  };

  // Setează / șterge override pe O ZI (PUT cu null șterge)
  const setClientOverrideDay = async (clientName, day, val) => {
    try {
      const body = { client_name: clientName, date: day, min_people: val };
      await api.put("/management/client-overrides", body);
      setClientOverrides((prev) => {
        const next = { ...prev };
        if (val == null || val === "") {
          if (next[clientName]) {
            const inner = { ...next[clientName] };
            delete inner[day];
            if (Object.keys(inner).length === 0) {
              delete next[clientName];
            } else {
              next[clientName] = inner;
            }
          }
        } else {
          if (!next[clientName]) next[clientName] = {};
          next[clientName] = { ...next[clientName], [day]: parseInt(val) };
        }
        return next;
      });
    } catch (e) {
      toast.error("Eroare la salvare override client");
    }
  };

  // Helper: regie este în pauză pe ziua dStr?
  const isRegiePausedOn = (r, dStr) => {
    if (!r) return false;
    if (r.paused === true && (!Array.isArray(r.pauses) || r.pauses.length === 0)) return true;
    for (const p of (r.pauses || [])) {
      const sd = p.start_date || "";
      const ed = p.end_date || "9999-12-31";
      if (sd && sd <= dStr && dStr <= ed) return true;
    }
    return false;
  };

  const needs = useMemo(() => {
    // Pentru fiecare zi: necesar regie = sumă min_people pe regiile ACTIVE care au săptămână în care e ziua
    // necesar proiecte = sumă min_people pentru proiectele cu entry pe acea zi (active)
    const regieNeed = {};
    const projNeed = {};
    const clientNeed = {}; // suma override-urilor active per client în acea zi
    const available = {};
    const totalActive = fieldPersonsCount;

    // Helper rapid: ce clienți au override activ în ziua d?
    const overriddenClientsForDay = (dStr) => {
      const set = new Set();
      for (const [name, byDate] of Object.entries(clientOverrides || {})) {
        if (byDate && byDate[dStr] != null) set.add(name);
      }
      return set;
    };

    for (const d of days) {
      const dStr = `${month}-${String(d).padStart(2, "0")}`;
      const overrClients = overriddenClientsForDay(dStr);

      // Adună override-urile clienților pentru ziua respectivă
      let cn = 0;
      for (const name of overrClients) {
        cn += parseInt((clientOverrides[name] || {})[dStr]) || 0;
      }
      clientNeed[d] = cn;

      // 🟢 Aliniere cu Planificator: Duminică sau sărbătoare națională → fără necesar
      const dObj = new Date(y, m - 1, d);
      const dow = dObj.getDay();
      const isSunday = dow === 0;
      const isSaturday = dow === 6;
      const hol = holidaysByDate[dStr];
      const isNational = hol && hol.type === "national";
      if (isSunday || isNational) {
        regieNeed[d] = 0;
        projNeed[d] = 0;
        const inVac = summaryRows.onVacationField[d] || 0;
        const assigned = summaryRows.totalAssigned[d] || 0;
        available[d] = Math.max(0, totalActive - inVac - assigned);
        continue;
      }

      // Helper: e proiectul în demontaj pe ziua respectivă? (aliniat 1:1 cu Planificator:
      // demontajul e O SINGURĂ ZI — ziua imediat după finalizare/end_date, nu o fereastră de zile)
      const isProjectInDismantleOn = (proj, iso) => {
        if (!proj) return false;
        const finRaw = proj.finalization?.finalized_at || proj.status_changed_at || proj.end_date || null;
        if (!finRaw) return false;
        let dismantleDay = null;
        try {
          const fd = new Date(String(finRaw).slice(0, 10) + "T00:00:00");
          if (!isNaN(fd.getTime())) {
            fd.setDate(fd.getDate() + 1);
            dismantleDay = localIso(fd);
          }
        } catch { /* skip */ }
        if (!dismantleDay) return false;
        const cat = String(proj.category || "");
        if (cat === "6.1") return iso === dismantleDay;
        if (proj.end_date && proj.end_date < iso) return iso === dismantleDay;
        return false;
      };

      // 1. Regie need: sare peste regiile clienților cu override pe ziua d
      //    🟢 Aliniere cu Planificator: SÂMBĂTĂ → regiile sunt opționale, fără necesar
      let rn = 0;
      if (!isSaturday) for (const r of regii.filter((x) => x.active !== false)) {
        const sd = r.start_date;
        const ed = r.end_date;
        const ok = (!sd || sd <= dStr) && (r.indeterminate_end || !ed || dStr <= ed);
        if (!ok) continue;
        // Pauză: dacă regia e în pauză pe această zi, nu mai necesită oameni
        if (isRegiePausedOn(r, dStr)) continue;
        const cName = _regieClient(r);
        if (cName && overrClients.has(cName)) continue; // override acoperă acest necesar
        // Day override priority
        const dayOv = dayOverrides.regii?.[r.id]?.[dStr];
        let minNeed;
        if (dayOv?.min != null) {
          minNeed = dayOv.min;
        } else {
          const weeks = regieWeeksMap[r.id] || [];
          const dayObj = new Date(y, m - 1, d);
          const monday = new Date(dayObj);
          monday.setDate(dayObj.getDate() - ((dayObj.getDay() + 6) % 7));
          const ws = localIso(monday);
          const w = weeks.find((wk) => wk.week_start === ws);
          // 🟢 Aliniere: respectă default_min_people_by_day[dow] (zilnic diferit)
          minNeed = (w?.min_people != null) ? Number(w.min_people) : Number(regieDefaultMinForIso(r, dStr) || 0);
        }
        // 🟢 Aliniere cu Planificator: necesarul BRUT (suma minimelor), nu deficit
        rn += minNeed;
      }

      // 2. Project need: prioritate day override → project.min_people → entry.min_people
      //    🟢 Aliniere cu Planificator: proiectele în DEMONTAJ → fără necesar
      //    🟢 Aliniere cu Planificator: SÂMBĂTA planificarea e 100% liberă → fără necesar nici la proiecte
      let pn = 0;
      const seen = new Set();
      // 🟢 Aliniere 1:1 cu Planificator: doar proiectele cu ENTRY pe ziua respectivă,
      //    cascade day_override → project.min_people → entry.min_people, sumă BRUTĂ (nu deficit)
      if (!isSaturday) for (const e of entries) {
        if (e.is_regie) continue;
        if ((e.start_date || "") <= dStr && dStr <= (e.end_date || "")) {
          const key = e.project_id || e.id;
          if (seen.has(key)) continue;
          seen.add(key);
          const proj = e.project_id ? projects.find((p) => p.id === e.project_id) : null;
          const cName = _projClient(proj);
          if (cName && overrClients.has(cName)) continue;
          // 🟢 Skip dacă proiect e în demontaj
          if (isProjectInDismantleOn(proj, dStr)) continue;
          const dayOv = e.project_id ? dayOverrides.projects?.[e.project_id]?.[dStr] : null;
          let minNeed = 0;
          if (dayOv?.min != null) {
            minNeed = Number(dayOv.min);
          } else if (e.project_id && proj?.min_people) {
            minNeed = Number(proj.min_people);
          } else if (e.min_people != null && e.min_people > 0) {
            minNeed = Number(e.min_people);
          }
          pn += minNeed;
        }
      }
      // 3. Available
      const inVac = summaryRows.onVacationField[d] || 0;
      const assigned = summaryRows.totalAssigned[d] || 0;
      available[d] = Math.max(0, totalActive - inVac - assigned);
      regieNeed[d] = rn;
      projNeed[d] = pn;
    }
    return { regieNeed, projNeed, clientNeed, available };
  }, [days, regii, regieWeeksMap, projects, entries, sortedPersons, summaryRows, y, m, month, dayOverrides, clientOverrides, holidaysByDate]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          {/* Month + Year selectors (în loc de input type=month) */}
          {(() => {
            const ROMANIAN_MONTHS = ["Ianuarie", "Februarie", "Martie", "Aprilie", "Mai", "Iunie", "Iulie", "August", "Septembrie", "Octombrie", "Noiembrie", "Decembrie"];
            const yyyy = parseInt(month.slice(0, 4)) || new Date().getFullYear();
            const mm = parseInt(month.slice(5, 7)) || (new Date().getMonth() + 1);
            const thisYear = new Date().getFullYear();
            const years = [];
            for (let y = thisYear - 3; y <= thisYear + 3; y++) years.push(y);
            const fmt = (y, m) => `${y}-${String(m).padStart(2, "0")}`;
            return (
              <div className="flex items-center gap-1.5" data-testid="people-grid-month-picker">
                <button
                  type="button"
                  onClick={() => shiftMonth(-1)}
                  className="border rounded-md w-9 h-[34px] text-sm bg-white hover:bg-zinc-50 hover:border-zinc-400 inline-flex items-center justify-center font-bold text-zinc-700 transition-colors"
                  title="Luna anterioară (← săgeată stânga pe tastatură)"
                  data-testid="people-grid-prev-month"
                >‹</button>
                <select
                  value={mm}
                  onChange={(e) => setMonth(fmt(yyyy, parseInt(e.target.value)))}
                  className="border rounded-md px-2 py-1.5 text-sm bg-white hover:bg-zinc-50"
                  data-testid="people-grid-month-select"
                >
                  {ROMANIAN_MONTHS.map((mn, i) => <option key={i+1} value={i+1}>{mn}</option>)}
                </select>
                <select
                  value={yyyy}
                  onChange={(e) => setMonth(fmt(parseInt(e.target.value), mm))}
                  className="border rounded-md px-2 py-1.5 text-sm bg-white hover:bg-zinc-50"
                  data-testid="people-grid-year-select"
                >
                  {years.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
                <button
                  type="button"
                  onClick={() => shiftMonth(1)}
                  className="border rounded-md w-9 h-[34px] text-sm bg-white hover:bg-zinc-50 hover:border-zinc-400 inline-flex items-center justify-center font-bold text-zinc-700 transition-colors"
                  title="Luna următoare (→ săgeată dreapta pe tastatură)"
                  data-testid="people-grid-next-month"
                >›</button>
                {!isCurrentMonth && (
                  <button
                    type="button"
                    onClick={() => {
                      const d = new Date();
                      setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
                    }}
                    className="border border-emerald-300 text-emerald-700 hover:bg-emerald-50 rounded-md px-2 py-1.5 text-xs font-semibold ml-1"
                    title="Sari la luna curentă"
                    data-testid="people-grid-today"
                  >Azi</button>
                )}
              </div>
            );
          })()}
          <span className="text-xs text-zinc-500">{sortedPersons.length} persoane</span>
          {isCurrentMonth && (
            <span className="text-xs px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 font-bold border border-emerald-200" data-testid="people-free-today">
              🟢 {freeTodayPersons.length} {freeTodayPersons.length === 1 ? "persoană liberă" : "persoane libere"} azi
            </span>
          )}
          {isCurrentMonth && freeTodayPersons.length > 0 && canDo("MANAGEMENT", "edit") && (
            <Button size="sm" onClick={() => setBulkOpen(true)} className="bg-amber-500 hover:bg-amber-600 h-8" data-testid="people-bulk-assign">
              <Sparkles size={13} className="mr-1"/> Atribuie pe regie
            </Button>
          )}
        </div>
        <Input
          placeholder="Caută persoană…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-xs"
          data-testid="people-grid-search"
        />
      </div>

      <div className="text-xs text-zinc-500">
        Click pe o celulă → atribuie/editează proiect sau regie pentru persoana respectivă în acea zi.
      </div>

      <div className="card-flat overflow-auto max-h-[calc(100vh-220px)]" data-testid="people-grid-table">
        <table className="text-sm border-collapse min-w-full">
          <thead className="sticky top-0 z-30 bg-white shadow-sm">
            {/* === RÂND KW (numerele săptămânilor) === */}
            <tr className="bg-cyan-50/60 border-b border-cyan-200">
              <th className="sticky left-0 bg-cyan-50 p-1.5 z-40 text-[10px] font-bold text-cyan-800 text-left whitespace-nowrap" title="Săptămâni calendaristice (ISO 8601 — KW)">📅 Săptămâni</th>
              {weekGroups.map((g) => (
                <th
                  key={`kw-${g.firstIso}`}
                  colSpan={g.cells.length}
                  className="text-center text-[11px] font-extrabold text-cyan-800 bg-cyan-50/80 border-r-2 border-cyan-300 px-1 py-1 whitespace-nowrap"
                  title={`Săptămâna ${g.kw} din ${g.year}`}
                  data-testid={`kw-header-${g.kw}-${g.year}`}
                >
                  KW {g.kw}
                </th>
              ))}
            </tr>
            {/* === RÂND NECESAR REGII === */}
            <tr className="bg-amber-50/80">
              <th className="sticky left-0 bg-amber-50 p-1.5 z-40 text-[10px] font-bold text-amber-800 text-left whitespace-nowrap" title="Necesarul minim TOTAL pe regii (suma minimelor) — identic cu Planificatorul">🟧 Necesar regii</th>
              {weekCells.map((cell) => {
                if (!cell.inMonth) {
                  return (
                    <th
                      key={`gh-${cell.iso}`}
                      className="bg-white text-zinc-400 text-[10px] text-center font-normal border-r border-zinc-200 min-w-[34px] border-b border-[var(--border)]"
                      data-testid={`ghost-day-${cell.iso}`}
                      title={`${cell.d}/${cell.monthIdx + 1} — în afara lunii curente`}
                    >{cell.d}</th>
                  );
                }
                const d = cell.d;
                const wd = new Date(y, m - 1, d).getDay();
                const dStr = `${month}-${String(d).padStart(2, "0")}`;
                const hol = holidaysByDate[dStr];
                const blackCell = wd === 0 || (hol && hol.type === "national");
                const v = needs.regieNeed[d] || 0;
                return (
                  <th key={d} className={`text-center text-[11px] font-bold ${blackCell ? "bg-zinc-900/95 text-white" : "text-amber-700"} border-r border-amber-100 min-w-[34px]`} data-testid={`need-regii-${d}`}>
                    {v > 0 ? v : "·"}
                  </th>
                );
              })}
            </tr>
            {/* === RÂND NECESAR PROIECTE === */}
            <tr className="bg-blue-50/80">
              <th className="sticky left-0 bg-blue-50 p-1.5 z-40 text-[10px] font-bold text-blue-800 text-left whitespace-nowrap" title="Necesarul minim TOTAL pe proiecte (suma minimelor) — identic cu Planificatorul; sare peste demontaj, duminici, sărbători">🟦 Necesar proiecte</th>
              {weekCells.map((cell) => {
                if (!cell.inMonth) {
                  return (
                    <th
                      key={`gh-${cell.iso}`}
                      className="bg-white text-zinc-400 text-[10px] text-center font-normal border-r border-zinc-200 min-w-[34px] border-b border-[var(--border)]"
                      data-testid={`ghost-day-${cell.iso}`}
                      title={`${cell.d}/${cell.monthIdx + 1} — în afara lunii curente`}
                    >{cell.d}</th>
                  );
                }
                const d = cell.d;
                const wd = new Date(y, m - 1, d).getDay();
                const dStr = `${month}-${String(d).padStart(2, "0")}`;
                const hol = holidaysByDate[dStr];
                const blackCell = wd === 0 || (hol && hol.type === "national");
                const v = needs.projNeed[d] || 0;
                return (
                  <th key={d} className={`text-center text-[11px] font-bold ${blackCell ? "bg-zinc-900/95 text-white" : "text-blue-700"} border-r border-blue-100 min-w-[34px]`} data-testid={`need-proj-${d}`}>
                    {v > 0 ? v : "·"}
                  </th>
                );
              })}
            </tr>
            {/* === RÂND TOTAL NECESAR === */}
            <tr className="bg-zinc-100">
              <th className="sticky left-0 bg-zinc-100 p-1.5 z-40 text-[10px] font-bold text-zinc-800 text-left whitespace-nowrap">🎯 Total necesar</th>
              {weekCells.map((cell) => {
                if (!cell.inMonth) {
                  return (
                    <th
                      key={`gh-${cell.iso}`}
                      className="bg-white text-zinc-400 text-[10px] text-center font-normal border-r border-zinc-200 min-w-[34px] border-b border-[var(--border)]"
                      data-testid={`ghost-day-${cell.iso}`}
                      title={`${cell.d}/${cell.monthIdx + 1} — în afara lunii curente`}
                    >{cell.d}</th>
                  );
                }
                const d = cell.d;
                const wd = new Date(y, m - 1, d).getDay();
                const dStr = `${month}-${String(d).padStart(2, "0")}`;
                const hol = holidaysByDate[dStr];
                const blackCell = wd === 0 || (hol && hol.type === "national");
                const total = (needs.regieNeed[d] || 0) + (needs.projNeed[d] || 0) + (needs.clientNeed[d] || 0);
                const have = summaryRows.totalAssigned[d] || 0;
                const short = total > have;
                return (
                  <th key={d} className={`text-center text-[11px] font-extrabold ${blackCell ? "bg-zinc-900/95 text-white" : short ? "text-red-600 bg-red-50" : "text-emerald-700 bg-emerald-50"} border-r border-zinc-200 min-w-[34px]`} data-testid={`need-total-${d}`}>
                    {total > 0 ? total : "·"}
                  </th>
                );
              })}
            </tr>
            {/* === RÂND TOTAL DISPONIBILI (active - concediu) === */}
            <tr className="bg-zinc-50">
              <th className="sticky left-0 bg-zinc-50 p-1.5 z-40 text-[10px] font-bold text-zinc-700 text-left whitespace-nowrap" title="Total persoane care POT lucra azi: active minus cei în concediu (indiferent dacă sunt deja alocați)">👥 Total disponibili</th>
              {weekCells.map((cell) => {
                if (!cell.inMonth) {
                  return (
                    <th
                      key={`gh-${cell.iso}`}
                      className="bg-white text-zinc-400 text-[10px] text-center font-normal border-r border-zinc-200 min-w-[34px] border-b border-[var(--border)]"
                      data-testid={`ghost-day-${cell.iso}`}
                      title={`${cell.d}/${cell.monthIdx + 1} — în afara lunii curente`}
                    >{cell.d}</th>
                  );
                }
                const d = cell.d;
                const wd = new Date(y, m - 1, d).getDay();
                const dStr = `${month}-${String(d).padStart(2, "0")}`;
                const hol = holidaysByDate[dStr];
                const blackCell = wd === 0 || (hol && hol.type === "national");
                const totalAvail = Math.max(0, fieldPersonsCount - (summaryRows.onVacationField[d] || 0));
                return (
                  <th key={d} className={`text-center text-[11px] font-extrabold ${blackCell ? "bg-zinc-900/95 text-white" : "text-zinc-800"} border-r border-zinc-200 min-w-[34px]`} data-testid={`total-avail-${d}`} title={`${fieldPersonsCount} teren (fără birou) − ${summaryRows.onVacationField[d] || 0} concediu = ${totalAvail} disponibili`}>
                    {totalAvail > 0 ? totalAvail : "·"}
                  </th>
                );
              })}
            </tr>
            {/* === RÂND DIFERENȚĂ (total disponibili - total necesar) === */}
            <tr className="bg-white">
              <th className="sticky left-0 bg-white p-1.5 z-40 text-[10px] font-bold text-zinc-700 text-left whitespace-nowrap" title="Diferența: Total disponibili minus Total necesar — verde dacă acoperă, roșu dacă lipsesc oameni">⚖️ Diferență</th>
              {weekCells.map((cell) => {
                if (!cell.inMonth) {
                  return (
                    <th
                      key={`gh-${cell.iso}`}
                      className="bg-white text-zinc-400 text-[10px] text-center font-normal border-r border-zinc-200 min-w-[34px] border-b border-[var(--border)]"
                      data-testid={`ghost-day-${cell.iso}`}
                      title={`${cell.d}/${cell.monthIdx + 1} — în afara lunii curente`}
                    >{cell.d}</th>
                  );
                }
                const d = cell.d;
                const wd = new Date(y, m - 1, d).getDay();
                const dStr = `${month}-${String(d).padStart(2, "0")}`;
                const hol = holidaysByDate[dStr];
                const blackCell = wd === 0 || (hol && hol.type === "national");
                const totalAvail = Math.max(0, fieldPersonsCount - (summaryRows.onVacationField[d] || 0));
                const totalNeed = (needs.regieNeed[d] || 0) + (needs.projNeed[d] || 0) + (needs.clientNeed[d] || 0);
                const balance = totalAvail - totalNeed;
                const showBal = totalNeed > 0 || totalAvail > 0;
                if (!showBal) {
                  return <th key={d} className={`text-center text-[11px] ${blackCell ? "bg-zinc-900/95 text-white" : "text-zinc-300"} border-r border-zinc-200 min-w-[34px]`}>·</th>;
                }
                const cls = blackCell
                  ? "bg-zinc-900/95 text-white"
                  : balance >= 0
                    ? "bg-emerald-50 text-emerald-700 border-emerald-300"
                    : "bg-red-50 text-red-700 border-red-300";
                return (
                  <th key={d} className={`text-center text-[11px] font-extrabold ${cls} border-r min-w-[34px]`} data-testid={`balance-${d}`} title={`${totalAvail} disponibili − ${totalNeed} necesari = ${balance >= 0 ? "+" : ""}${balance}`}>
                    {balance >= 0 ? `+${balance}` : balance}
                  </th>
                );
              })}
            </tr>
            {/* === RÂND DISPONIBIL === */}
            <tr className="bg-emerald-50">
              <th className="sticky left-0 bg-emerald-50 p-1.5 z-40 text-[10px] font-bold text-emerald-800 text-left whitespace-nowrap">🟢 Disponibili azi</th>
              {weekCells.map((cell) => {
                if (!cell.inMonth) {
                  return (
                    <th
                      key={`gh-${cell.iso}`}
                      className="bg-white text-zinc-400 text-[10px] text-center font-normal border-r border-zinc-200 min-w-[34px] border-b border-[var(--border)]"
                      data-testid={`ghost-day-${cell.iso}`}
                      title={`${cell.d}/${cell.monthIdx + 1} — în afara lunii curente`}
                    >{cell.d}</th>
                  );
                }
                const d = cell.d;
                const wd = new Date(y, m - 1, d).getDay();
                const dStr = `${month}-${String(d).padStart(2, "0")}`;
                const hol = holidaysByDate[dStr];
                const blackCell = wd === 0 || (hol && hol.type === "national");
                const v = needs.available[d] || 0;
                return (
                  <th key={d} className={`text-center text-[11px] font-bold ${blackCell ? "bg-zinc-900/95 text-white" : v === 0 ? "text-zinc-400" : "text-emerald-700"} border-r border-emerald-100 min-w-[34px]`} data-testid={`need-avail-${d}`}>
                    {v > 0 ? v : "·"}
                  </th>
                );
              })}
            </tr>
            {/* === RÂND ZILE LUNI === */}
            <tr>
              <th className="sticky left-0 bg-white p-2 border-b border-[var(--border)] min-w-[180px] text-left z-40">
                <span className="small-label">Plan</span>
              </th>
              {weekCells.map((cell) => {
                if (!cell.inMonth) {
                  return (
                    <th
                      key={`gh-${cell.iso}`}
                      className="bg-white text-zinc-400 text-[10px] text-center font-normal border-r border-zinc-200 min-w-[34px] border-b border-[var(--border)]"
                      data-testid={`ghost-day-${cell.iso}`}
                      title={`${cell.d}/${cell.monthIdx + 1} — în afara lunii curente`}
                    >{cell.d}</th>
                  );
                }
                const d = cell.d;
                const w = new Date(y, m - 1, d).getDay();
                const isSun = w === 0;
                const isSat = w === 6;
                const dStr = `${month}-${String(d).padStart(2, "0")}`;
                const hol = holidaysByDate[dStr];
                const isNat = hol && hol.type === "national";
                const blackBg = isSun || isNat;
                const today = localIso(new Date());
                const isToday = dStr === today;
                return (
                  <th key={d} className={`p-1 border-b border-[var(--border)] text-xs font-semibold min-w-[34px] relative ${blackBg ? "bg-zinc-900 text-white" : isSat ? "bg-zinc-100" : "bg-white"} ${isToday ? "ring-2 ring-[var(--primary)] ring-inset" : ""}`}>
                    <div>{d}</div>
                    <div className={`text-[9px] font-normal ${blackBg ? "text-zinc-300" : "text-zinc-400"}`}>
                      {["D", "L", "M", "M", "J", "V", "S"][w]}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {/* === SUMAR PLANIFICAT === */}
            <tr className="bg-zinc-50 border-b-2 border-zinc-300">
              <td className="sticky left-0 bg-zinc-50 p-2 z-10 text-[11px] font-bold text-zinc-700">📊 Avem (planificați)</td>
              {weekCells.map((cell) => {
                if (!cell.inMonth) {
                  return (
                    <th
                      key={`gh-${cell.iso}`}
                      className="bg-white text-zinc-400 text-[10px] text-center font-normal border-r border-zinc-200 min-w-[34px] border-b border-[var(--border)]"
                      data-testid={`ghost-day-${cell.iso}`}
                      title={`${cell.d}/${cell.monthIdx + 1} — în afara lunii curente`}
                    >{cell.d}</th>
                  );
                }
                const d = cell.d;
                const dStr = `${month}-${String(d).padStart(2, "0")}`;
                const wd = new Date(y, m - 1, d).getDay();
                const hol = holidaysByDate[dStr];
                const isNat = hol && hol.type === "national";
                const blackCell = wd === 0 || isNat;
                const v = summaryRows.totalAssigned[d] || 0;
                return (
                  <td key={d} className={`text-center text-xs font-bold ${blackCell ? "bg-zinc-900/95 text-white" : "text-zinc-700"} border-r border-zinc-200`} data-testid={`summary-have-${d}`}>
                    {v > 0 ? v : ""}
                  </td>
                );
              })}
            </tr>
            <tr className="bg-amber-50/40 border-b border-amber-200">
              <td className="sticky left-0 bg-amber-50 p-2 z-10 text-[11px] font-bold text-amber-800">  └ pe regii</td>
              {weekCells.map((cell) => {
                if (!cell.inMonth) {
                  return (
                    <th
                      key={`gh-${cell.iso}`}
                      className="bg-white text-zinc-400 text-[10px] text-center font-normal border-r border-zinc-200 min-w-[34px] border-b border-[var(--border)]"
                      data-testid={`ghost-day-${cell.iso}`}
                      title={`${cell.d}/${cell.monthIdx + 1} — în afara lunii curente`}
                    >{cell.d}</th>
                  );
                }
                const d = cell.d;
                const dStr = `${month}-${String(d).padStart(2, "0")}`;
                const wd = new Date(y, m - 1, d).getDay();
                const hol = holidaysByDate[dStr];
                const isNat = hol && hol.type === "national";
                const blackCell = wd === 0 || isNat;
                const v = summaryRows.regieAssigned[d] || 0;
                return (
                  <td key={d} className={`text-center text-xs ${blackCell ? "bg-zinc-900/95 text-white" : "text-amber-700"} border-r border-amber-100`} data-testid={`summary-regie-${d}`}>
                    {v > 0 ? v : ""}
                  </td>
                );
              })}
            </tr>
            <tr className="bg-orange-50 border-b border-orange-200">
              <td className="sticky left-0 bg-orange-50 p-2 z-10 text-[11px] font-bold text-orange-800">🏖 Concediu</td>
              {weekCells.map((cell) => {
                if (!cell.inMonth) {
                  return (
                    <th
                      key={`gh-${cell.iso}`}
                      className="bg-white text-zinc-400 text-[10px] text-center font-normal border-r border-zinc-200 min-w-[34px] border-b border-[var(--border)]"
                      data-testid={`ghost-day-${cell.iso}`}
                      title={`${cell.d}/${cell.monthIdx + 1} — în afara lunii curente`}
                    >{cell.d}</th>
                  );
                }
                const d = cell.d;
                const dStr = `${month}-${String(d).padStart(2, "0")}`;
                const wd = new Date(y, m - 1, d).getDay();
                const hol = holidaysByDate[dStr];
                const isNat = hol && hol.type === "national";
                const blackCell = wd === 0 || isNat;
                const v = summaryRows.onVacation[d] || 0;
                return (
                  <td key={d} className={`text-center text-xs ${blackCell ? "bg-zinc-900/95 text-white" : "text-orange-700"} border-r border-orange-100`}>
                    {v > 0 ? v : ""}
                  </td>
                );
              })}
            </tr>

            {/* === REGII === */}
            {regiiGrouped.length > 0 && (
              <tr className="bg-amber-50/60">
                <td className="sticky left-0 bg-amber-50/60 p-1 z-10 text-[10px] uppercase tracking-wider font-bold text-amber-800 border-b border-amber-200" colSpan={1}>🟧 Regii</td>
                <td colSpan={weekCells.length} className="border-b border-amber-200"/>
              </tr>
            )}
            {regiiGrouped.map(([firma, list]) => (
              <React.Fragment key={`group-${firma}`}>
                <tr className="bg-amber-100/40">
                  <td className="sticky left-0 bg-amber-100/60 p-1.5 z-10 text-[10px] font-bold text-amber-900 border-b border-amber-200" colSpan={1}>
                    📁 {firma} <span className="text-amber-600 font-normal">({list.length})</span>
                  </td>
                  <td colSpan={weekCells.length} className="border-b border-amber-200 bg-amber-100/30"/>
                </tr>
                {list.map((rg) => (
                  <tr key={`reg-${rg.id || rg.name}`} className="border-b border-[var(--border)] hover:bg-amber-50/40" data-testid={`regie-row-${rg.id || rg.name}`}>
                    <td className="sticky left-0 bg-white p-2 z-10 max-w-[180px]">
                      <div className="flex items-center gap-2 pl-3">
                        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: rg.color || "#F59E0B" }}/>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (rg.id) {
                              navigate(`/proiecte?regie=${rg.id}&edit=1`);
                            } else {
                              setDetailDialog({ kind: "regie", data: regii.find((x) => x.id === rg.id) || rg });
                            }
                          }}
                          className="text-xs font-medium truncate text-left hover:text-[var(--primary)] hover:underline"
                          title={`${rg.name} · click pentru a deschide regia în „Proiecte" și a o edita`}
                          data-testid={`regie-name-btn-${rg.id || rg.name}`}
                        >
                          {rg.name}
                        </button>
                      </div>
                    </td>
                    {weekCells.map((cell) => {
                if (!cell.inMonth) {
                  return renderRegieGhostCell(cell, rg.id, rg.color);
                }
                const d = cell.d;
                      const dStr = `${month}-${String(d).padStart(2, "0")}`;
                      const wd = new Date(y, m - 1, d).getDay();
                      const hol = holidaysByDate[dStr];
                      const isNat = hol && hol.type === "national";
                      const blackCell = wd === 0 || isNat;
                      const isSat = wd === 6;
                      const ent = dayHasEntry(rg.entries, dStr);
                      const count = ent ? (ent.person_ids || []).length : 0;
                      // Regie active only within start_date..end_date (unless indeterminate_end)
                      const sd = rg.start_date;
                      const ed = rg.end_date;
                      const inRange = (!sd || sd <= dStr) && (rg.indeterminate_end || !ed || dStr <= ed);
                      // Săptămâna ce conține ziua = luni
                      const dayObj = new Date(y, m - 1, d);
                      const monday = new Date(dayObj);
                      monday.setDate(dayObj.getDate() - ((dayObj.getDay() + 6) % 7));
                      const ws = localIso(monday);
                      const wkObj = (regieWeeksMap[rg.id] || []).find((w) => w.week_start === ws);
                      const dayOv = dayOverrides.regii?.[rg.id]?.[dStr];
                      // Verifică dacă există override la nivel de client pentru această zi
                      const rgClient = _regieClient(rg);
                      const clientOvActive = !!(rgClient && (clientOverrides[rgClient] || {})[dStr] != null);
                      // Pauză activă pe această zi?
                      const isPaused = isRegiePausedOn(rg, dStr);
                      // Resolution priority: day override → week → default
                      // Necesarul se aplică DOAR în perioada activă a regiei (și nu în pauză)
                      const minNeed = (!inRange || isPaused) ? 0
                                     : (dayOv?.min != null) ? dayOv.min
                                     : (wkObj?.min_people != null) ? wkObj.min_people
                                     : (rg.default_min_people || 0);
                      const isOverride = inRange && !isPaused && (dayOv?.min != null);
                      return (
                        <td
                        key={d}
                        className={`p-0.5 border-r border-[var(--border)]/30 h-8 relative group/cell ${blackCell ? "bg-zinc-900/95" : isSat ? "bg-zinc-50" : ""} ${isPaused ? "bg-amber-50/80 ring-1 ring-amber-300/60" : ""}`}
                          data-testid={`regie-cell-${rg.id || rg.name}-${d}`}
                          title={isPaused ? `⏸ Regia e în pauză pe ${dStr} — fără necesar` : undefined}
                        >
                          {ent && (
                            <div
                              onClick={() => onTopRowClick(ent, dStr, true, rg.id, rg.name, rg.color)}
                              className="absolute inset-0 rounded-sm cursor-pointer hover:brightness-95 relative overflow-hidden"
                              style={{ background: ent.color || rg.color || "#F59E0B" }}
                              title={`${rg.name} · ${count}/${minNeed} ${count === 1 ? "persoană" : "persoane"} alocate din ${minNeed || 0} necesare`}
                            >
                              {/* Linia diagonală */}
                              {minNeed > 0 && (
                                <span className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(to top right, transparent calc(50% - 0.5px), rgba(255,255,255,0.55) calc(50% - 0.5px), rgba(255,255,255,0.55) calc(50% + 0.5px), transparent calc(50% + 0.5px))" }} />
                              )}
                              {/* Stânga-sus: alocați */}
                              <span className={`absolute top-0 left-0.5 text-[9px] font-extrabold leading-none pt-0.5 ${count >= (minNeed || 0) ? "text-white" : "text-red-100"}`}>
                                {count}
                              </span>
                              {/* Dreapta-jos: necesar */}
                              {minNeed > 0 && (
                                <span className="absolute bottom-0 right-0.5 text-[9px] font-bold text-white/90 leading-none pb-0.5">
                                  {minNeed}
                                </span>
                              )}
                            </div>
                          )}
                          {!ent && minNeed > 0 && !blackCell && (
                            <div
                              onClick={() => onTopRowClick(null, dStr, true, rg.id, rg.name, rg.color)}
                              className="absolute inset-0 flex items-center justify-center text-[10px] text-amber-600/70 cursor-pointer hover:bg-amber-50"
                              title={`Necesar: ${minNeed} pers · click pentru a atribui`}
                            >
                              {minNeed}
                            </div>
                          )}
                          {!ent && minNeed === 0 && !blackCell && inRange && (
                            <div
                              onClick={() => onTopRowClick(null, dStr, true, rg.id, rg.name, rg.color)}
                              className="absolute inset-0 cursor-pointer hover:bg-zinc-50"
                            />
                          )}
                          {!ent && minNeed === 0 && !blackCell && !inRange && (
                            <div
                              className="absolute inset-0 bg-zinc-50/60"
                              title="Regia nu este activă în această zi"
                            />
                          )}
                          {!blackCell && inRange && rg.id && canDo("MANAGEMENT", "edit") && (
                            <DayMinSpinner
                              testId={`regie-spin-${rg.id}-${d}`}
                              isOverride={isOverride}
                              minVal={minNeed}
                              onUp={() => updateDayOverride("regie", rg.id, dStr, "min", minNeed + 1)}
                              onDown={() => minNeed > 0 && updateDayOverride("regie", rg.id, dStr, "min", minNeed - 1)}
                            />
                          )}
                          {blackCell && (
                            <div
                              onClick={() => onTopRowClick(null, dStr, true, rg.id, rg.name, rg.color)}
                              className="absolute inset-0 cursor-pointer hover:bg-zinc-800"
                            />
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </React.Fragment>
            ))}

            {/* === PROIECTE === */}
            {projectsInMonth.length > 0 && (
              <tr className="bg-blue-50/60">
                <td className="sticky left-0 bg-blue-50/60 p-1 z-10 text-[10px] uppercase tracking-wider font-bold text-blue-800 border-b border-blue-200" colSpan={1}>🟦 Proiecte</td>
                <td colSpan={weekCells.length} className="border-b border-blue-200"/>
              </tr>
            )}
            {projectsInMonth.map((pr) => {
              const projObj = projects.find((p) => p.id === pr.id);
              const summary = buildProjectSummary(projObj);
              // 🚙 Info pentru eticheta de lângă ultima/prima zi: mașină + șofer + EKAS
              let prFirstDay = null, prLastDay = null, prLastEnt = null;
              for (const e of (pr.entries || [])) {
                const sd = e.start_date || ""; const ed = e.end_date || sd;
                if (sd && (!prFirstDay || sd < prFirstDay)) prFirstDay = sd;
                if (ed && (!prLastDay || ed > prLastDay)) { prLastDay = ed; prLastEnt = e; }
              }
              let siteInfoLabel = null;
              if (prLastEnt) {
                const carId = (prLastEnt.car_ids || [])[0];
                const carObj = carId ? allCars.find((c) => c.id === carId) : null;
                const carLbl = carObj ? (carObj.intern_number ? `#${carObj.intern_number}` : (carObj.numar_inmatriculare || "")) : null;
                const drvId = carId ? (prLastEnt.car_drivers || {})[carId] : null;
                const drvObj = drvId ? persons.find((p) => p.id === drvId) : null;
                const drvLbl = drvObj ? `${drvObj.nume || ""} ${drvObj.prenume || ""}`.trim() : null;
                const ekasN = (projObj?.zones?.length) || Number(projObj?.quick_scope?.ekas_zones || 0) || 0;
                const parts = [];
                if (carLbl) parts.push(`🚙 ${carLbl}`);
                if (drvLbl) parts.push(`🧑‍✈️ ${drvLbl}`);
                if (ekasN > 0) parts.push(`🟦 ${ekasN} EKAS`);
                if (parts.length) siteInfoLabel = parts.join(" · ");
              }
              let prSpanDays = 0;
              try { prSpanDays = prFirstDay && prLastDay ? (new Date(prLastDay) - new Date(prFirstDay)) / 86400000 + 1 : 0; } catch { prSpanDays = 0; }
              const showLeftInfo = siteInfoLabel && prSpanDays > 14;
              return (
              <tr key={`proj-${pr.id || pr.name}`} className="border-b border-[var(--border)] hover:bg-blue-50/40" data-testid={`project-row-${pr.id || pr.name}`}>
                <td className="sticky left-0 bg-white p-2 z-10 max-w-[220px]">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: pr.color || "#3B82F6" }}/>
                    {pr.id ? (
                      <span className="inline-flex items-center gap-1.5 min-w-0">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/proiecte?project=${pr.id}&edit=1`);
                          }}
                          className="text-xs font-semibold truncate text-left hover:text-[var(--primary)] hover:underline"
                          title={`${pr.name} · click pentru a deschide proiectul în „Proiecte" și a-l edita`}
                          data-testid={`project-name-btn-${pr.id}`}
                        >
                          {pr.name}
                        </button>
                        <ToolColorDots projectId={pr.id} colors={toolColorsOfEntries(pr.entries)} onChanged={load} />
                      </span>
                    ) : (
                      <span className="text-xs font-semibold truncate" title={pr.name}>{pr.name}</span>
                    )}
                  </div>
                  {summary && summary.hasContent && (
                    <div className="ml-4 mt-0.5 space-y-0.5" data-testid={`project-summary-${pr.id || pr.name}`}>
                      {summary.zoneBadge && (
                        <div className="text-[10px] text-blue-700 font-semibold truncate" title={summary.zoneBadge}>
                          🟦 {summary.zoneBadge}
                        </div>
                      )}
                      {summary.materialLines && summary.materialLines.slice(0, 6).map((ml) => (
                        <div
                          key={`ml-${pr.id || pr.name}-${ml.material}`}
                          className="text-[10px] text-zinc-700 truncate"
                          title={ml.text}
                          data-testid={`project-material-${pr.id || pr.name}-${ml.material}`}
                        >
                          🔨 {ml.text}
                        </div>
                      ))}
                      {summary.materialLines && summary.materialLines.length > 6 && (
                        <div className="text-[9px] text-zinc-400 italic">+{summary.materialLines.length - 6} altele…</div>
                      )}
                    </div>
                  )}
                </td>
                {weekCells.map((cell) => {
                if (!cell.inMonth) {
                  return renderProjectGhostCell(cell, pr.id, pr.color);
                }
                const d = cell.d;
                  const dStr = `${month}-${String(d).padStart(2, "0")}`;
                  const wd = new Date(y, m - 1, d).getDay();
                  const hol = holidaysByDate[dStr];
                  const isNat = hol && hol.type === "national";
                  const blackCell = wd === 0 || isNat;
                  const isSat = wd === 6;
                  const ent = dayHasEntry(pr.entries, dStr);
                  const count = ent ? (ent.person_ids || []).length : 0;
                  // Culoarea setului de scule: din ORICE entry al zilei care are allocation_color
                  const toolEnt = (pr.entries || []).find((e) => {
                    const sd = e.start_date || "";
                    const ed = e.end_date || sd;
                    return sd <= dStr && dStr <= ed && (e.allocation_color || (e.allocation_colors || []).length);
                  });
                  const toolCols = toolEnt ? ((toolEnt.allocation_colors || []).length ? toolEnt.allocation_colors : [toolEnt.allocation_color]) : [];
                  const toolHex = toolColorsBg(toolCols, TOOL_SET_HEX);
                  // Resolution: day override → entry.min_people → project.min_people
                  const projObj = projects.find((p) => p.id === pr.id);
                  const dayOv = dayOverrides.projects?.[pr.id]?.[dStr];
                  const minNeed = (dayOv?.min != null) ? dayOv.min
                                : (ent?.min_people != null) ? ent.min_people
                                : (projObj?.min_people || 0);
                  const isOverride = dayOv?.min != null;
                  // Verifică dacă există override la nivel de client pentru această zi
                  const projClient = _projClient(projObj);
                  const clientOvActive = !!(projClient && (clientOverrides[projClient] || {})[dStr] != null);
                  // === MEASUREMENT MARKER ===
                  // Dacă e ziua imediat următoare end_date și proiectul cere măsurătoare,
                  // afișăm un overlay semi-transparent „📐 demontaj/măsurătoare".
                  let showMeasMarker = false;
                  if (
                    !ent &&
                    !blackCell &&
                    projObj &&
                    projObj.requires_post_measurement === true &&
                    !projObj.measurement_completed_at &&
                    projObj.end_date
                  ) {
                    try {
                      const ed = new Date(projObj.end_date + "T00:00:00");
                      const next = new Date(ed);
                      next.setDate(ed.getDate() + 1);
                      const nextStr = localIso(next);
                      if (dStr === nextStr) showMeasMarker = true;
                    } catch (e) { /* skip */ }
                  }
                  return (
                    <td
                      key={d}
                      className={`p-0.5 border-r border-[var(--border)]/30 h-7 relative group/cell ${blackCell ? "bg-zinc-900/95" : isSat ? "bg-zinc-50" : ""}`}
                      data-testid={`project-cell-${pr.id || pr.name}-${d}`}
                    >
                      {ent && (
                        <div
                          onClick={() => onTopRowClick(ent, dStr, false, pr.id, pr.name, pr.color)}
                          className="absolute inset-0 rounded-sm cursor-pointer hover:brightness-95 overflow-hidden"
                          style={{ background: toolHex || (ent.allocation_color && TOOL_SET_HEX[ent.allocation_color]) || ent.color || pr.color || "#3B82F6" }}
                          title={`${pr.name} · ${count}/${minNeed || 0} ${count === 1 ? "persoană" : "persoane"} alocate din ${minNeed || 0} necesare${toolCols.length ? ` · 🎨 set scule: ${toolCols.join(" + ")}` : ""}`}
                        >
                          {minNeed > 0 && (
                            <span className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(to top right, transparent calc(50% - 0.5px), rgba(255,255,255,0.55) calc(50% - 0.5px), rgba(255,255,255,0.55) calc(50% + 0.5px), transparent calc(50% + 0.5px))" }} />
                          )}
                          <span className={`absolute top-0 left-0.5 text-[9px] font-extrabold leading-none pt-0.5 ${toolCols.includes("galben") ? "text-yellow-950" : count >= (minNeed || 0) ? "text-white" : "text-red-100"}`}>
                            {count}
                          </span>
                          {minNeed > 0 && (
                            <span className={`absolute bottom-0 right-0.5 text-[9px] font-bold leading-none pb-0.5 ${toolCols.includes("galben") ? "text-yellow-950" : "text-white/90"}`}>
                              {minNeed}
                            </span>
                          )}
                        </div>
                      )}
                      {/* 🚙 Eticheta cu mașina/șoferul/EKAS — în dreapta ultimei zile */}
                      {siteInfoLabel && dStr === prLastDay && (
                        <div
                          className="absolute left-full top-1/2 -translate-y-1/2 z-30 whitespace-nowrap text-[9px] font-bold text-zinc-700 bg-white/90 rounded px-1 pointer-events-none"
                          data-testid={`site-info-right-${pr.id}`}
                        >
                          {siteInfoLabel}
                        </div>
                      )}
                      {/* >2 săptămâni → și în stânga primei zile */}
                      {showLeftInfo && dStr === prFirstDay && (
                        <div
                          className="absolute right-full top-1/2 -translate-y-1/2 z-30 whitespace-nowrap text-[9px] font-bold text-zinc-700 bg-white/90 rounded px-1 pointer-events-none"
                          data-testid={`site-info-left-${pr.id}`}
                        >
                          {siteInfoLabel}
                        </div>
                      )}
                      {showMeasMarker && (
                        <div
                          onClick={() => onTopRowClick(null, dStr, false, pr.id, pr.name, pr.color)}
                          className="absolute inset-0 rounded-sm cursor-pointer overflow-hidden flex items-center justify-center"
                          style={{
                            background: pr.color || projObj.color || "#3B82F6",
                            opacity: 0.18,
                          }}
                          title={`📐 ${pr.name} · zi de demontaj/măsurătoare (după finalizare ${projObj.end_date})`}
                          data-testid={`project-measurement-marker-${pr.id}-${d}`}
                        >
                          <span className="text-[9px] font-bold opacity-95" style={{ color: "rgba(0,0,0,0.7)" }}>📐</span>
                        </div>
                      )}
                      {!ent && !blackCell && (
                        <div onClick={() => onTopRowClick(null, dStr, false, pr.id, pr.name, pr.color)} className="absolute inset-0 cursor-pointer hover:bg-zinc-50" title={minNeed > 0 ? `Necesar: ${minNeed}` : undefined}/>
                      )}
                      {!blackCell && pr.id && ent && canDo("MANAGEMENT", "edit") && (
                        <DayMinSpinner
                          testId={`project-spin-${pr.id}-${d}`}
                          isOverride={isOverride}
                          minVal={minNeed}
                          onUp={() => updateDayOverride("project", pr.id, dStr, "min", minNeed + 1)}
                          onDown={() => minNeed > 0 && updateDayOverride("project", pr.id, dStr, "min", minNeed - 1)}
                        />
                      )}
                      {blackCell && (
                        <div onClick={() => onTopRowClick(null, dStr, false, pr.id, pr.name, pr.color)} className="absolute inset-0 cursor-pointer hover:bg-zinc-800"/>
                      )}
                    </td>
                  );
                })}
              </tr>
              );
            })}

            {/* === PERSOANE === */}
            <tr className="bg-zinc-100">
              <td className="sticky left-0 bg-zinc-100 p-1 z-10 text-[10px] uppercase tracking-wider font-bold text-zinc-700 border-b border-zinc-300" colSpan={1}>👥 Persoane</td>
              <td colSpan={weekCells.length} className="border-b border-zinc-300"/>
            </tr>
            {sortedPersons.length === 0 && (
              <tr><td colSpan={weekCells.length + 1} className="p-6 text-center text-zinc-400 text-sm">Nicio persoană activă.</td></tr>
            )}
            {sortedPersons.map((p) => (
              <tr key={p.id} className="border-b border-[var(--border)] hover:bg-zinc-50/50" data-testid={`people-row-${p.id}`}>
                <td className="sticky left-0 bg-white p-2 z-10">
                  <div className="flex items-center gap-2">
                    {p.color && <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: p.color }}/>}
                    <Link to={`/personal/${p.id}`} className="text-sm font-medium hover:underline truncate max-w-[160px]" title={`${p.nume} ${p.prenume}`}>
                      {p.nume} {p.prenume}
                    </Link>
                  </div>
                </td>
                {weekCells.map((cell) => {
                if (!cell.inMonth) {
                  return (
                    <th
                      key={`gh-${cell.iso}`}
                      className="bg-white text-zinc-400 text-[10px] text-center font-normal border-r border-zinc-200 min-w-[34px] border-b border-[var(--border)]"
                      data-testid={`ghost-day-${cell.iso}`}
                      title={`${cell.d}/${cell.monthIdx + 1} — în afara lunii curente`}
                    >{cell.d}</th>
                  );
                }
                const d = cell.d;
                  const dStr = `${month}-${String(d).padStart(2, "0")}`;
                  const wd = new Date(y, m - 1, d).getDay();
                  const hol = holidaysByDate[dStr];
                  const isNat = hol && hol.type === "national";
                  const isSun = wd === 0;
                  const isSat = wd === 6;
                  const blackCell = isSun || isNat;
                  const onVac = isOnVacation(p.id, dStr);
                  const dayEntries = entriesForPersonDay(p.id, dStr);
                  const main = dayEntries[0];
                  const extra = dayEntries.length - 1;
                  const today = localIso(new Date());
                  const isToday = dStr === today;
                  const h = (hoursMap[p.id] || {})[dStr];
                  const worked = h?.hours || 0;
                  const driven = h?.hours_driven || 0;
                  // Culoarea: prioritate calendar.color > proiect.color (din calendar.project_id) > schedule.color > grey
                  let cellColor = h?.color;
                  if (!cellColor && h?.project_id) {
                    const proj = projects.find((x) => x.id === h.project_id);
                    if (proj?.color) cellColor = proj.color;
                  }
                  if (!cellColor) cellColor = main?.color || (main?.is_regie ? "#F59E0B" : main ? "#3B82F6" : null);
                  return (
                    <td
                      key={d}
                      onClick={() => onCellClick(p.id, d)}
                      className={`p-0.5 border-r border-[var(--border)]/30 h-9 cursor-pointer relative ${onVac && !main && worked === 0 ? "bg-zinc-900/95" : blackCell ? "bg-zinc-900/95" : isSat ? "bg-zinc-50" : ""} ${isToday ? "ring-1 ring-inset ring-[var(--primary)]/40" : ""} hover:brightness-95`}
                      data-testid={`people-cell-${p.id}-${d}`}
                      title={(h?.project_label || main?.label || "") + (worked > 0 ? ` · ${worked}h lucrate` : "") + (driven > 0 ? ` · ${driven}h conduse` : (onVac ? " · Concediu aprobat — indisponibil" : ""))}
                    >
                      {onVac && !main && worked === 0 && (
                        <div className="h-full w-full flex items-center justify-center text-[9px] font-extrabold text-white" data-testid={`people-cell-${p.id}-${d}-vacation`}>CO</div>
                      )}
                      {(main || worked > 0) && (
                        worked > 0 && driven > 0 ? (
                          <div className="h-full rounded-sm overflow-hidden text-white" style={{ background: cellColor || "#9CA3AF" }}>
                            <div className="h-1/2 flex items-center justify-center text-[8px] font-bold leading-none border-b border-white/30 bg-black/15" title={`${driven}h conduse`}>
                              <span className="inline-flex items-center gap-0.5">🚗 {driven.toFixed(driven % 1 === 0 ? 0 : 1)}</span>
                            </div>
                            <div className="h-1/2 flex items-center justify-center text-[10px] font-extrabold leading-none" title={`${worked}h lucrate`}>
                              {worked.toFixed(worked % 1 === 0 ? 0 : 1)}
                              {extra > 0 && <span className="ml-0.5 text-[7px] opacity-90">+{extra}</span>}
                            </div>
                          </div>
                        ) : (
                          <div
                            className="h-full rounded-sm flex items-center justify-center text-[10px] font-extrabold text-white truncate px-0.5"
                            style={{ background: cellColor || "#9CA3AF" }}
                          >
                            {worked > 0 ? worked.toFixed(worked % 1 === 0 ? 0 : 1) : (main?.label || "").slice(0, 4)}
                            {extra > 0 && <span className="ml-0.5 text-[8px] opacity-90">+{extra}</span>}
                          </div>
                        )
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {dialog && (
        <ScheduleDialog
          mode={dialog.mode} entry={dialog.entry}
          initialStart={dialog.start_date} initialEnd={dialog.end_date}
          prefilledPersonId={dialog.prefilledPerson}
          prefillEntry={dialog.prefillEntry}
          projects={projects}
          regii={regii}
          allPersons={persons}
          allCars={allCars}
          dailyNeeds={[]}
          month={month}
          existingEntries={entries}
          regieWeeksMap={regieWeeksMap}
          onClose={() => setDialog(null)}
          onSaved={() => { setDialog(null); load(); }}
          onProjectsRefresh={async () => { const { data } = await api.get("/projects"); setProjects(data); }}
        />
      )}

      {bulkOpen && (
        <BulkAssignDialog
          regii={regii.filter((r) => r.active !== false)}
          freePersons={freeTodayPersons}
          today={todayIso}
          onClose={() => setBulkOpen(false)}
          onDone={() => { setBulkOpen(false); load(); }}
        />
      )}

      {hoursDialog && (
        <LogHoursDialog
          {...hoursDialog}
          projects={projects}
          regii={regii}
          onClose={() => setHoursDialog(null)}
          onSaved={() => { setHoursDialog(null); load(); }}
        />
      )}

      {detailDialog && (
        <DetailsDialog
          kind={detailDialog.kind}
          data={detailDialog.data}
          onClose={() => setDetailDialog(null)}
        />
      )}

      {clientOvDialog && (
        <ClientOverrideDialog
          clientName={clientOvDialog.clientName}
          defaultStart={clientOvDialog.defaultStart}
          defaultEnd={clientOvDialog.defaultEnd}
          defaultValue={clientOvDialog.defaultValue}
          onClose={() => setClientOvDialog(null)}
          onSaved={() => { setClientOvDialog(null); reloadClientOverrides(); }}
        />
      )}
    </div>
  );
}

export default function Management() {
  return (
    <div data-testid="page-management">
      <div className="mb-6">
        <h1 className="font-display text-3xl font-extrabold">Management</h1>
        <p className="text-sm text-zinc-500 mt-1">Plan lunar regii + proiecte · jurnal intern</p>
      </div>
      <Tabs defaultValue="people">
        <TabsList>
          <TabsTrigger value="people" data-testid="tab-people"><UsersIcon size={14} className="mr-1.5"/> Plan persoane</TabsTrigger>
          <TabsTrigger value="hours" data-testid="tab-hours"><CalendarDays size={14} className="mr-1.5"/> Ore lucrate / conduse</TabsTrigger>
          <TabsTrigger value="charts" data-testid="tab-charts"><BarChart3 size={14} className="mr-1.5"/> Statistici</TabsTrigger>
          <TabsTrigger value="containers" data-testid="tab-containers"><Package2 size={14} className="mr-1.5"/> Containere saci</TabsTrigger>
          <TabsTrigger value="jurnal" data-testid="tab-jurnal"><BookOpen size={14} className="mr-1.5"/> Jurnal</TabsTrigger>
        </TabsList>
        <TabsContent value="people"><PeoplePlanGrid /></TabsContent>
        <TabsContent value="hours"><HoursGridWrapper /></TabsContent>
        <TabsContent value="charts"><Charts /></TabsContent>
        <TabsContent value="containers"><Containers /></TabsContent>
        <TabsContent value="jurnal"><Jurnal /></TabsContent>
      </Tabs>
    </div>
  );
}
